import { Api, ApiError } from "../api";
import { commissionArgs, commissionStatus, CommissionEvent, leftToShip, nextEvent, shipmentLines, totals } from "../commissions";
import { empty, h, label, pill, replace } from "../dom";
import { money, qty, shortDate, today, unitPrice } from "../format";
import { navigate, reload } from "../nav";
import { viewMode, words } from "../orders";
import type { ActResult, AgentOrder, AgentShipment, Me, Page } from "../types";
import { plain } from "../words";
import { can, confirmButton, modeNotice, outcome, toggle } from "./shared";

// Commissions (agent_commissions, commissions.read): Broker/Agent orders, where the customer buys from the supplier at
// Tare's price and the supplier pays Tare the spread. Per order, what shipped and each shipment's commission: when it's
// expected and where it stands (earned, invoiced, collected, paid, late). Someone with commissions.write records what
// happened to a shipment's commission (record_commission); someone with orders.write records a shipment the reader
// missed (record_agent_shipment) or a whole order (record_agent_order, on /commissions/new). The filters live in the
// address, as on Orders.

const STATUSES = ["OPEN", "ALL", "RECEIVED", "PARTIALLY_SHIPPED", "SHIPPED", "CLOSED"];
const EVENT_WORDS: Record<CommissionEvent, string> = { INVOICED: "Invoiced", COLLECTED: "Collected by the supplier", PAID: "Paid" };

/** A press that records something: shows the outcome, and reloads the screen when it's applied. */
async function act(api: Api, tool: string, args: Record<string, unknown>, result: HTMLElement, buttons: HTMLButtonElement[]): Promise<void> {
  buttons.forEach((b) => (b.disabled = true));
  try {
    const r = await api.tool<ActResult>(tool, args);
    if (r.status === "APPLIED") return reload();
    replace(result, outcome(r, "Recorded."));
  } catch (err) {
    replace(result, h("p", { class: "error" }, plain((err as ApiError).message)));
  }
  buttons.forEach((b) => (b.disabled = false));
}

/** The form under a shipment for recording its commission's next step (any step can be chosen). */
function commissionForm(api: Api, o: AgentOrder, s: AgentShipment): HTMLElement {
  const id = `c${o.customerPoId}-${s.releaseNo}`;
  const result = h("div", { class: "result", "aria-live": "polite" });
  const event = h("select", { id: `${id}-event` }, (Object.keys(EVENT_WORDS) as CommissionEvent[]).map((e) =>
    h("option", { value: e, selected: e === nextEvent(s) }, EVENT_WORDS[e])));
  const invoice = h("input", { id: `${id}-invoice`, type: "text", maxlength: 40, autocomplete: "off", placeholder: "e.g. 1042" });
  const date = h("input", { id: `${id}-date`, type: "date", value: today() });
  const month = h("input", { id: `${id}-month`, type: "month", value: today().slice(0, 7) });
  const amount = h("input", { id: `${id}-amount`, type: "text", inputmode: "decimal", autocomplete: "off", value: s.commission.toFixed(2) });
  const note = h("input", { id: `${id}-note`, type: "text", maxlength: 500, autocomplete: "off", placeholder: "Optional" });
  const field = (input: HTMLElement, text: string) => h("div", { class: "field" }, h("label", { for: input.id }, text), input);
  const fInvoice = field(invoice, "Tare's invoice number");
  const fDate = field(date, "On");
  const fMonth = field(month, "Month collected");
  const fAmount = field(amount, "Amount paid");
  const show = () => {
    const e = event.value as CommissionEvent;
    fInvoice.hidden = e !== "INVOICED";
    fDate.hidden = e === "COLLECTED";
    fMonth.hidden = e !== "COLLECTED";
    fAmount.hidden = e !== "PAID";
  };
  event.addEventListener("change", show);
  show();
  const built = () => commissionArgs(o, s, event.value as CommissionEvent,
    { invoice: invoice.value, date: date.value, month: month.value, amount: amount.value, note: note.value });
  const record: HTMLButtonElement = confirmButton("Record it", "Press again to record it", () => {
    const b = built();
    if ("args" in b) void act(api, "record_commission", b.args, result, [record]);
  }, "button-primary", () => {
    const b = built();
    if ("missing" in b) replace(result, h("p", { class: "error" }, b.missing));
    else replace(result);
    return "args" in b;
  });
  return h("div", { class: "filters" }, field(event, "What happened"), fInvoice, fDate, fMonth, fAmount, field(note, "Note"), record, result);
}

/** Record a shipment by hand (when its BOL didn't come by mail): date, BOL, carrier and the quantity per SKU. */
function shipmentForm(api: Api, o: AgentOrder): HTMLElement {
  const id = `s${o.customerPoId}`;
  const result = h("div", { class: "result", "aria-live": "polite" });
  const shipped = h("input", { id: `${id}-on`, type: "date", value: today() });
  const bol = h("input", { id: `${id}-bol`, type: "text", maxlength: 60, autocomplete: "off", placeholder: "As written on it" });
  const carrier = h("input", { id: `${id}-carrier`, type: "text", maxlength: 100, autocomplete: "off" });
  const note = h("input", { id: `${id}-note`, type: "text", maxlength: 500, autocomplete: "off", placeholder: "Optional" });
  const left = leftToShip(o);
  const qtys = left.map((l, i) => h("input", { id: `${id}-q${i}`, type: "text", inputmode: "numeric", autocomplete: "off",
    value: l.leftEa ? String(l.leftEa) : "", "aria-label": `Shipped EA of ${l.sku}` }));
  const field = (input: HTMLElement, text: string) => h("div", { class: "field" }, h("label", { for: input.id }, text), input);
  const lines = () => shipmentLines(left.map((l, i) => ({ sku: l.sku, qty: qtys[i].value })));
  const record: HTMLButtonElement = confirmButton("Record the shipment", "Press again to record it", () => {
    const ls = lines();
    if (Array.isArray(ls)) {
      void act(api, "record_agent_shipment", { customer_po_id: o.customerPoId, shipped_on: shipped.value || undefined,
        ...(bol.value.trim() ? { bol: bol.value.trim() } : {}), ...(carrier.value.trim() ? { carrier: carrier.value.trim() } : {}),
        ...(note.value.trim() ? { note: note.value.trim() } : {}), lines: ls }, result, [record]);
    }
  }, "button-primary", () => {
    const ls = lines();
    if (!Array.isArray(ls)) replace(result, h("p", { class: "error" }, ls.problem));
    else if (!bol.value.trim()) replace(result, h("p", { class: "error" }, "Give the BOL number, as written on it."));
    else replace(result);
    return Array.isArray(ls) && !!bol.value.trim();
  });
  return h("div", { class: "stack-tight" },
    h("p", { class: "caption" }, "A BOL copied to Tare by mail records its shipment by itself. Record one here when it didn't come. " +
      "The quantities start at what's left to ship."),
    h("div", { class: "filters" }, field(shipped, "Shipped on"), field(bol, "BOL"), field(carrier, "Carrier"), field(note, "Note")),
    h("table", { class: "tbl" },
      h("thead", null, h("tr", null, h("th", { scope: "col" }, "SKU"), h("th", { scope: "col", class: "r" }, "Left to ship"),
        h("th", { scope: "col", class: "r" }, "Shipped EA"))),
      h("tbody", null, left.map((l, i) => h("tr", null, h("td", { class: "num" }, l.sku), h("td", { class: "num r" }, qty(l.leftEa)),
        h("td", { class: "r" }, qtys[i]))))),
    h("div", { class: "row-act" }, record), result);
}

function orderPanel(api: Api, me: Me, o: AgentOrder, param: string): HTMLElement {
  const canRecord = can(me, "commissions.write");
  const showCost = o.lines.some((l) => typeof l.unitCost === "number");
  return h("section", { class: "panel decision", "aria-label": `${o.agentOrder ?? o.poNumber}` },
    h("div", { class: "row" },
      h("div", null,
        h("h2", { class: "heading" }, o.customer?.name ?? "", " → ", o.supplier?.name ?? ""),
        h("p", { class: "caption" }, h("span", { class: "num" }, o.agentOrder ?? ""), " · PO ",
          h("a", { class: "num", href: `/order/${o.customerPoId}${param}` }, o.poNumber),
          o.poDate ? ` · dated ${shortDate(o.poDate)}` : "", o.wanted ? ` · wanted ${shortDate(o.wanted)}` : "")),
      pill(words(o.status).toLowerCase(), o.status === "CLOSED" ? "good" : "neutral")),
    h("div", { class: "filters" },
      h("div", { class: "field" }, label("Expected"), h("span", { class: "number-md" }, money(o.commissionExpected))),
      h("div", { class: "field" }, label("Earned so far"), h("span", { class: "number-md" }, money(o.commissionEarned))),
      h("div", { class: "field" }, label("Paid"), h("span", { class: "number-md" }, money(o.commissionPaid)))),
    o.commissionTerms ? h("p", { class: "caption" }, plain(o.commissionTerms)) : null,
    h("div", { class: "scroll" }, h("table", { class: "tbl" },
      h("thead", null, h("tr", null,
        h("th", { scope: "col" }, "SKU"), h("th", { scope: "col", class: "r" }, "Ordered"), h("th", { scope: "col", class: "r" }, "Shipped"),
        h("th", { scope: "col", class: "r" }, "Price"), showCost ? h("th", { scope: "col", class: "r" }, "Cost") : null,
        showCost ? h("th", { scope: "col", class: "r" }, "Spread / EA") : null)),
      h("tbody", null, o.lines.map((l) => h("tr", null,
        h("td", { class: "num" }, l.sku), h("td", { class: "num r" }, qty(l.qtyEa)), h("td", { class: "num r" }, qty(l.shippedEa)),
        h("td", { class: "num r" }, unitPrice(l.unitPrice)),
        showCost ? h("td", { class: "num r" }, unitPrice(l.unitCost)) : null,
        showCost ? h("td", { class: "num r" }, unitPrice(l.spreadPerEa)) : null))))),
    o.shipments.length
      ? h("div", { class: "scroll" }, h("table", { class: "tbl" },
          h("thead", null, h("tr", null,
            h("th", { scope: "col" }, "Shipment"), h("th", { scope: "col" }, "Shipped"), h("th", { scope: "col" }, "BOL"),
            h("th", { scope: "col", class: "r" }, "EA"), h("th", { scope: "col", class: "r" }, "Commission"), h("th", { scope: "col" }, "Status"),
            h("th", { scope: "col" }, "Expected"), h("th", { scope: "col" }, "Invoice"), h("th", { scope: "col" }, "Collected"),
            h("th", { scope: "col" }, "Paid"))),
          h("tbody", null, o.shipments.map((s) => {
            const st = commissionStatus(s.status);
            return h("tr", null,
              h("td", { class: "num" }, String(s.releaseNo)), h("td", { class: "num" }, shortDate(s.shippedOn)),
              h("td", { class: "num" }, s.bol ?? ""), h("td", { class: "num r" }, qty(s.qtyEa, "")),
              h("td", { class: "num r" }, money(s.commission)), h("td", null, pill(st.text.toLowerCase(), st.tone)),
              h("td", { class: "num" }, shortDate(s.expectedPayOn) || h("span", { class: "muted" }, "—")),
              h("td", { class: "num" }, s.invoice ? `${s.invoice}${s.invoicedOn ? `, ${shortDate(s.invoicedOn)}` : ""}` : h("span", { class: "muted" }, "—")),
              h("td", { class: "num" }, s.collectedMonth ?? h("span", { class: "muted" }, "—")),
              h("td", { class: "num" }, s.paidOn ? `${money(s.paidAmount)}, ${shortDate(s.paidOn)}` : h("span", { class: "muted" }, "—")));
          }))))
      : h("p", { class: "caption" }, "Nothing has shipped yet."),
    canRecord
      ? o.shipments.filter((s) => nextEvent(s)).map((s) =>
          toggle(`Record shipment ${s.releaseNo}'s commission`, () => commissionForm(api, o, s)))
      : null,
    can(me, "orders.write") && o.status !== "CLOSED" ? toggle("Record a shipment", () => shipmentForm(api, o)) : null);
}

export async function commissions(api: Api, me: Me, query: URLSearchParams): Promise<HTMLElement> {
  const customer = (query.get("customer") ?? "").slice(0, 100);
  const supplier = (query.get("supplier") ?? "").slice(0, 100);
  const status = STATUSES.includes(query.get("status") ?? "") ? query.get("status")! : "OPEN";
  const cursor = /^[0-9]{1,6}$/.test(query.get("cursor") ?? "") ? query.get("cursor")! : undefined;
  const asked = ["LIVE", "SIMULATION"].includes(query.get("mode") ?? "") ? query.get("mode")! : undefined;
  const page = await api.tool<Page<AgentOrder>>("agent_commissions", {
    status, limit: 50, ...(customer ? { customer } : {}), ...(supplier ? { supplier } : {}), ...(cursor ? { cursor } : {}),
    ...(asked ? { mode: asked } : {}),
  });
  const view = viewMode(query, page.mode);
  const t = totals(page.rows);

  const customerInput = h("input", { id: "customer", type: "search", value: customer, placeholder: "Creative, Bunzl…", maxlength: 100 });
  const supplierInput = h("input", { id: "supplier", type: "search", value: supplier, placeholder: "Rusken…", maxlength: 100 });
  const statusSelect = h("select", { id: "status" }, STATUSES.map((s) => h("option", { value: s, selected: s === status }, s === "OPEN" ? "Open" : words(s))));
  const modeSelect = h("select", { id: "mode" }, (["LIVE", "SIMULATION"] as const).map((m) =>
    h("option", { value: m, selected: m === view.mode }, `${m === "LIVE" ? "Live orders" : "Simulation copies"}${m === page.mode ? " (the platform's mode)" : ""}`)));
  const go = () => navigate(`/commissions?${new URLSearchParams({
    ...(customerInput.value.trim() ? { customer: customerInput.value.trim() } : {}),
    ...(supplierInput.value.trim() ? { supplier: supplierInput.value.trim() } : {}), status: statusSelect.value,
    ...(modeSelect.value !== page.mode ? { mode: modeSelect.value } : {}) })}`);
  const form = h("form", { class: "filters", role: "search" },
    h("div", { class: "field" }, h("label", { for: "customer" }, "Customer"), customerInput),
    h("div", { class: "field" }, h("label", { for: "supplier" }, "Supplier"), supplierInput),
    h("div", { class: "field" }, h("label", { for: "status" }, "Orders"), statusSelect),
    h("div", { class: "field" }, h("label", { for: "mode" }, "Showing"), modeSelect),
    h("button", { type: "submit", class: "button" }, "Show"));
  form.addEventListener("submit", (e) => { e.preventDefault(); go(); });
  statusSelect.addEventListener("change", go);
  modeSelect.addEventListener("change", go);

  const tile = (name: string, figure: string, note: string, late = false) =>
    h("div", { class: "tile" }, label(name), h("span", { class: `number-md${late ? " late" : ""}` }, figure), h("span", { class: "caption" }, note));

  return h("div", { class: "page" },
    h("div", { class: "title-row" },
      h("h1", { class: "title" }, "Commissions"),
      can(me, "orders.write") ? h("a", { class: "button", href: "/commissions/new" }, "Record an agent order") : null),
    h("p", { class: "lede" }, page.rows.length
      ? `${page.rows.length}${page.next_cursor ? "+" : ""} Broker/Agent ${page.rows.length === 1 ? "order" : "orders"}` +
        `${status === "OPEN" ? " open" : ""}: ${money(t.earned)} earned, ${money(t.paid)} paid` +
        `${t.late ? `, ${t.late} late` : ""}${t.toInvoice ? `, ${t.toInvoice} to invoice` : ""}.`
      : "No Broker/Agent orders match."),
    form,
    view.other ? modeNotice(view.mode, page.mode) : null,
    page.rows.length
      ? [h("div", { class: "tiles" },
          tile("Expected", money(t.expected), "on these orders when all ships"),
          tile("Earned", money(t.earned), "on what has shipped"),
          tile("Paid", money(t.paid), "by the suppliers"),
          tile("Late", String(t.late), t.late ? "shipments past their expected date" : "none", t.late > 0)),
        ...page.rows.map((o) => orderPanel(api, me, o, view.param))]
      : empty("Nothing here", "Try another customer or supplier, or All for closed orders too."),
    page.next_cursor
      ? h("div", null, h("a", { class: "button", href: `/commissions?${new URLSearchParams({ ...(customer ? { customer } : {}),
          ...(supplier ? { supplier } : {}), status, cursor: page.next_cursor, ...(view.other ? { mode: view.mode } : {}) })}` }, "Next 50"))
      : null,
    label(view.mode === "SIMULATION" ? "Simulation copies" : "Live orders"));
}
